import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import {
  getAllProgrammes,
  deleteProgramme,
  PROGRAMME_ADDON_CODES,
} from './ProgrammeService';
import {
  Alert,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Container from 'components/Container';

const ProgrammeList = (): JSX.Element => {
  const [programmes, setProgrammes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    listProgrammes();
  }, []);

  function listProgrammes() {
    setLoading(true);
    getAllProgrammes()
      .then((response) => setProgrammes(response.data))
      .catch((error) => setErrorMessage(extractErrorMessage(error)))
      .finally(() => setLoading(false));
  }

  function selectProgramme(id: number) {
    navigate(`/system/dashboard/programme/${id}`);
  }

  const handleDeleteConfirmation = (id: number) => {
    setIdToDelete(id);
    setOpenDialog(true);
  };

  const handleDeleteItem = () => {
    deleteProgramme(idToDelete!)
      .then(() => listProgrammes())
      .catch((error) =>
        setErrorMessage(
          extractErrorMessage(error) ||
            'Error deleting programme. It may be referenced by other records.',
        ),
      );
    setOpenDialog(false);
  };

  /** Look up the scope and mode label for a given addonCode */
  function getMeta(addonCode: string) {
    return PROGRAMME_ADDON_CODES.find((p) => p.code === addonCode);
  }

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Typography color="text.primary">Programmes</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Programmes</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/system/dashboard/add-programme')}
          >
            Add Programme
          </Button>
        </Box>

        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        {loading && <p>Loading...</p>}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 750 }} aria-label="programmes table">
            <TableHead sx={{ bgcolor: 'alternate.main' }}>
              <TableRow>
                {[
                  'ID',
                  'Name',
                  'Addon Code',
                  'Mode',
                  'Scope',
                  'Status',
                  '',
                  '',
                ].map((h) => (
                  <TableCell key={h}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                  Are you sure you want to delete this programme?
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                  <Button onClick={handleDeleteItem} autoFocus>
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>

              {programmes
                .sort((a: any, b: any) => a.id - b.id)
                .map((item: any) => {
                  const meta = getMeta(item.addonCode);
                  return (
                    <TableRow
                      key={item.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                      }}
                      onClick={() => selectProgramme(item.id)}
                    >
                      <TableCell>{item.id}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{item.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {item.addonCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {meta?.mode ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {meta && (
                          <Chip
                            label={meta.scope}
                            size="small"
                            color={
                              meta.scope === 'BUSINESS' ? 'warning' : 'primary'
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color="primary"
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ cursor: 'pointer' }}
                        >
                          Edit
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConfirmation(item.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </SystemDashboard>
    </Container>
  );
};

export default ProgrammeList;
